import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { LeaseDealsModel, LeaseDealsPaths } from 'client/data/models/lease-deals';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { usePagination } from 'site-modules/lease-deals/components/lease-deals-pagination/use-pagination';
import { EDM_LEASE_DEALS_PAGINATION } from 'site-modules/lease-deals/constants/lease-deals-constants';
import { Pagination } from 'site-modules/shared/components/pagination/pagination';

import './lease-deals-pagination.scss';

const DESKTOP_NUM_CONFIG = {
  allAvailablePageNum: 6,
  startNumWithoutTruncate: 6,
  middleNumWithoutTruncate: 5,
  numLeftOfCurrentMiddle: 2,
  endNumWithoutTruncate: 5,
  totalMiddleNumWithoutTruncate: 5,
};
const MOBILE_NUM_CONFIG = {
  allAvailablePageNum: 4,
  startNumWithoutTruncate: 4,
  middleNumWithoutTruncate: 3,
  endNumWithoutTruncate: 2,
};

export const LeaseDealsPaginationUI = memo(
  ({ isMobile = false, totalCount: totalCountProp = undefined, selectedFilters = undefined, setModelValue }) => {
    const numConfig = isMobile ? MOBILE_NUM_CONFIG : DESKTOP_NUM_CONFIG;

    const {
      totalCount,
      pageStartItemNumber,
      pageEndItemNumber,
      totalPageNumber,
      currentPageNumber,
      paginationLinkBuilder,
      onClick,
    } = usePagination({ totalCount: totalCountProp, selectedFilters, setModelValue });

    return (
      <div
        className="lease-deals-pagination d-flex justify-content-center align-items-center flex-nowrap flex-column"
        data-tracking-parent={EDM_LEASE_DEALS_PAGINATION}
      >
        <div
          className={classnames(
            'lease-deals-pagination-inner d-flex flex-row align-items-center flex-nowrap justify-content-around bg-white',
            {
              'py-0_25': !isMobile,
              'py-0_5 lease-deals-pagination-inner-mobile': isMobile,
              'bg-transparent': totalPageNumber === 1,
              desktop: !isMobile,
            }
          )}
        >
          <Pagination
            currentNum={currentPageNumber}
            totalNum={totalPageNumber}
            paginationLinkBuilder={paginationLinkBuilder}
            className="d-flex justify-content-around align-items-center"
            listClassName="d-flex justify-content-around align-items-center w-100"
            arrowClassName="d-flex align-items-center text-cool-gray-30 size-16 px-1 px-md-1_5 py-0_5 py-md-0_25"
            numLinkClassname="num-link text-cool-gray-30"
            numLinkWrapperClassname={classnames('pagination-element pos-r', {
              'mx-md-1_25': !isMobile,
              'mx-1_25': isMobile,
            })}
            currentNumLinkClassname="fw-bold text-white"
            currentNumLinkWrapperClassname={classnames('pagination-element num-link-current', {
              'mx-md-1_25': !isMobile,
              'mx-1_25': isMobile,
            })}
            dotsClassname="dots pagination-element mx-0_25 medium text-cool-gray-30"
            onPageLinkClick={onClick}
            onArrowLinkClick={onClick}
            numConfig={numConfig}
            arrowsText={isMobile ? undefined : { right: 'Next', left: 'Previous' }}
            showDisabledArrows={totalPageNumber > 1}
            showLastNum={false}
            isTextForMobile={false}
            isSrp
          />
        </div>
        <div className="mt-1 small">
          <span className="text-nowrap text-cool-gray-40">
            Showing {pageStartItemNumber} - {pageEndItemNumber} out of {numberWithCommas(totalCount)} results
          </span>
        </div>
      </div>
    );
  }
);

LeaseDealsPaginationUI.displayName = 'LeaseDealsPaginationUI';

LeaseDealsPaginationUI.propTypes = {
  selectedFilters: PropTypes.shape({}),
  totalCount: PropTypes.number,
  isMobile: PropTypes.bool,
  setModelValue: PropTypes.func.isRequired,
};

const stateToPropsConfig = {
  totalCount: bindToPath(
    LeaseDealsPaths.getLeaseDealsPath(),
    LeaseDealsModel,
    ({ topDealsByModelCount }) => topDealsByModelCount,
    false
  ),
  selectedFilters: bindToPath(LeaseDealsPaths.getSelectedFiltersPath(), LeaseDealsModel, null, false),
};
export const LeaseDealsPagination = connectToModel(LeaseDealsPaginationUI, stateToPropsConfig);
